import type { FC } from 'react'

import { useEffect, useLayoutEffect, useRef } from 'react'
import { prefersReducedMotion } from '@/hooks/use-reduced-motion'

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

interface CountUpProps {
    value: number
    from?: number
    replay?: number
    duration?: number
    easing?: 'cubic' | 'expo'
    format?: (value: number) => string
    className?: string
}

const defaultFormat = (value: number): string => value.toFixed(2)

const EASINGS = {
    cubic: (t: number): number => 1 - Math.pow(1 - t, 3),
    expo: (t: number): number => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t))
}

/**
 * Animates a number to its new value by editing its own text node,
 * so the parent never re-renders per frame. The server renders the final value.
 */
const CountUp: FC<CountUpProps> = ({
    value,
    from,
    replay = 0,
    duration = 700,
    easing = 'cubic',
    format = defaultFormat,
    className
}) => {
    const ref = useRef<HTMLSpanElement>(null)
    const previous = useRef(value)
    const formatRef = useRef(format)

    useEffect(() => {
        formatRef.current = format
    })

    useIsomorphicLayoutEffect(() => {
        const node = ref.current?.firstChild
        const start = from ?? previous.current
        previous.current = value

        if (!node || start === value || prefersReducedMotion()) return

        const ease = EASINGS[easing]
        const began = performance.now()
        let frame = 0

        node.nodeValue = formatRef.current(start)

        const step = (now: number) => {
            const progress = Math.min(1, (now - began) / duration)
            node.nodeValue = formatRef.current(start + (value - start) * ease(progress))
            if (progress < 1) frame = requestAnimationFrame(step)
        }

        frame = requestAnimationFrame(step)

        return () => {
            cancelAnimationFrame(frame)
            node.nodeValue = formatRef.current(value)
        }
    }, [value, replay])

    return (
        <span ref={ref} className={className}>
            {format(value)}
        </span>
    )
}

export default CountUp
